import {
  createMutationKeys,
  createQueryKeys,
  mergeQueryKeys,
} from '@lukemorales/query-key-factory'
import { APIAxiosInstance } from '@core/utils/api-axios-instance'
import { RequestTypes } from './requestTypes'
import { createQueryKeyString } from '@core/react-query/queryParams'
import { attachAuditResultMediaTypes } from '@core/entities/audit/AuditResult'
import { s3Upload } from '@core/react-query/helpers'

const registerItemsQueryKeys = createQueryKeys('register-items', {
  byId: (payload: RequestTypes['getRegisterItemDetails']) => {
    return {
      queryKey: [payload.registerId, payload.registerItemId],
      queryFn: async () => {
        const { data } = await APIAxiosInstance.get(
          `registers/${payload.registerId}/items/${payload.registerItemId}`,
          {},
        )
        return data
      },
      contextQueries: {
        timeline: ({
          registerId,
          registerItemId,
          page,
          size,
        }: RequestTypes['getRegisterItemTimeline']) => ({
          queryKey: [registerId, registerItemId, page, size],
          queryFn: async ({ pageParam = 1 }) => {
            const queryString = createQueryKeyString({
              page: page ? page : pageParam,
              size,
            })
            const { data } = await APIAxiosInstance.get(
              `registers/${registerId}/items/${registerItemId}/audits${queryString}`,
            )
            await Promise.all(data.content.map(attachAuditResultMediaTypes))
            return data
          },
        }),
        linkedProcesses: ({
          registerId,
          registerItemId,
        }: RequestTypes['getRegisterItemLinkedProcesses']) => ({
          queryKey: [registerId, registerItemId],
          queryFn: async () => {
            const { data } = await APIAxiosInstance.get(
              `registers/${registerId}/items/${registerItemId}/linked-processes`,
            )
            return data
          },
        }),
        completedInspections: ({
          registerTemplateInspectionId,
          registerId,
          registerItemId,
          scheduledInspectionId,
          page,
          size,
          sort,
        }: RequestTypes['getRegisterItemCompletedInspections']) => ({
          queryKey: [
            registerId,
            registerItemId,
            scheduledInspectionId ?? 'all',
            registerTemplateInspectionId,
            page,
            size,
            sort,
          ],
          queryFn: async ({ pageParam = 1 }) => {
            const queryString = createQueryKeyString({
              page: page !== undefined && page !== null ? page : pageParam,
              size,
              sort,
              registerTemplateInspectionId: `inspection.${registerTemplateInspectionId}`,
              ...(scheduledInspectionId != null && {
                scheduledInspectionId,
              }),
            })
            const { data } = await APIAxiosInstance.get(
              `registers/${registerId}/items/${registerItemId}/completed-inspections${queryString}`,
            )
            return data
          },
        }),
        inspectionTasks: ({
          registerTemplateInspectionId,
          registerId,
          registerItemId,
          scheduledInspectionId,
          page,
          size,
          sort,
        }: RequestTypes['getRegisterItemTasks']) => ({
          queryKey: [
            registerId,
            registerItemId,
            scheduledInspectionId ?? 'all',
            registerTemplateInspectionId,
            page,
            size,
            sort,
          ],
          queryFn: async ({ pageParam = 1 }) => {
            const queryString = createQueryKeyString({
              page: page !== undefined && page !== null ? page : pageParam,
              size,
              sort,
              registerTemplateInspectionId: `inspection.${registerTemplateInspectionId}`,
              ...(scheduledInspectionId != null && {
                scheduledInspectionId,
              }),
            })
            const { data } = await APIAxiosInstance.get(
              `registers/${registerId}/items/${registerItemId}/tasks${queryString}`,
            )
            return data
          },
        }),
        attachments: ({
          registerId,
          registerItemId,
        }: RequestTypes['getRegisterItemAttachments']) => ({
          queryKey: [registerId, registerItemId],
          queryFn: async () => {
            const { data } = await APIAxiosInstance.get(
              `registers/${registerId}/items/${registerItemId}/attachments`,
            )
            return data
          },
        }),
        archivedAttachments: ({
          registerId,
          registerItemId,
          propertyId,
          page,
          size = 20,
        }: RequestTypes['getRegisterItemArchivedAttachments']) => ({
          queryKey: [
            registerId,
            registerItemId,
            propertyId ?? 'all',
            page,
            size,
          ],
          queryFn: async ({ pageParam = 1 }) => {
            const queryString = createQueryKeyString({
              page: page !== undefined && page !== null ? page : pageParam,
              size,
              ...(propertyId != null && { propertyId }),
            })
            const { data } = await APIAxiosInstance.get(
              `registers/${registerId}/items/${registerItemId}/attachments/archived${queryString}`,
            )
            return data
          },
        }),
      },
    }
  },
})

const registerItemsMutationKeys = createMutationKeys('register-items', {
  updateRegisterItem: {
    mutationKey: null,
    mutationFn: async ({
      registerId,
      registerItemId,
      ...rest
    }: RequestTypes['updateRegisterItem']) => {
      const { data } = await APIAxiosInstance.put(
        `registers/${registerId}/items/${registerItemId}`,
        {
          ...rest,
        },
      )
      return data
    },
  },
  updateRegisterItemAnnotations: {
    mutationKey: null,
    mutationFn: async ({
      registerId,
      registerItemId,
      ...rest
    }: RequestTypes['updateRegisterItemAnnotations']) => {
      const { data } = await APIAxiosInstance.put(
        `registers/${registerId}/items/${registerItemId}/annotations`,
        rest,
      )
      return data
    },
  },
  moveOrTransferItemsToDiagram: {
    mutationKey: null,
    mutationFn: async ({
      registerId,
      ...rest
    }: RequestTypes['moveOrTransferItemsToDiagram']) => {
      const { data } = await APIAxiosInstance.put(
        `registers/${registerId}/items/annotations`,
        rest,
      )
      return data
    },
  },
  overrideRegisterItemFormulaValue: {
    mutationKey: null,
    mutationFn: async ({
      registerId,
      registerItemId,
      data: payloadData,
    }: RequestTypes['overrideRegisterItemFormulaValue']) => {
      const { data } = await APIAxiosInstance.put(
        `registers/${registerId}/items/${registerItemId}/override-formula`,
        payloadData,
      )
      return data
    },
  },
  removeRegisterItemFormulaOverride: {
    mutationKey: null,
    mutationFn: async ({
      registerId,
      registerItemId,
      registerPropertyId,
    }: RequestTypes['removeRegisterItemFormulaOverride']) => {
      const { data } = await APIAxiosInstance.put(
        `registers/${registerId}/items/${registerItemId}/properties/${registerPropertyId}/remove-formula-override`,
        {},
      )
      return data
    },
  },
  createRegisterItem: {
    mutationKey: null,
    mutationFn: async ({
      registerId,
      ...rest
    }: RequestTypes['createRegisterItem']) => {
      const { data } = await APIAxiosInstance.post(
        `registers/${registerId}/items`,
        {
          ...rest,
        },
      )
      return data
    },
  },
  archiveRegisterItem: {
    mutationKey: null,
    mutationFn: async ({
      registerId,
      registerItemId,
      ...rest
    }: RequestTypes['archiveRegisterItem']) => {
      const { data } = await APIAxiosInstance.post(
        `registers/${registerId}/items/${registerItemId}/archive`,
        rest,
      )
      return data
    },
  },
  unarchiveRegisterItem: {
    mutationKey: null,
    mutationFn: async ({
      registerId,
      registerItemId,
      ...rest
    }: RequestTypes['unarchiveRegisterItem']) => {
      const { data } = await APIAxiosInstance.post(
        `registers/${registerId}/items/${registerItemId}/unarchive`,
        rest,
      )
      return data
    },
  },
  uploadRegisterItems: {
    mutationKey: null,
    mutationFn: async (variables: RequestTypes['importRegisterItemsCsv']) => {
      const registerItemsDocumentKey = await s3Upload({
        file: variables.file,
        folderName: variables.folderName,
      })
      const { data } = await APIAxiosInstance.post(
        `registers/${variables.registerId}/items/upload-async/csv`,
        {
          registerItemsDocumentKey,
        },
      )
      return data
    },
  },
  createRegisterItemScheduledInspection: {
    mutationKey: null,
    mutationFn: async ({
      registerId,
      registerItemId,
      data,
    }: RequestTypes['createRegisterItemScheduledInspection']) => {
      const { data: response } = await APIAxiosInstance.post(
        `registers/${registerId}/items/${registerItemId}/scheduled-inspections`,
        data,
      )
      return response
    },
  },
  updateRegisterItemScheduledInspection: {
    mutationKey: null,
    mutationFn: async ({
      registerId,
      registerItemId,
      scheduledInspectionId,
      data,
    }: RequestTypes['updateRegisterItemScheduledInspection']) => {
      const { data: response } = await APIAxiosInstance.put(
        `registers/${registerId}/items/${registerItemId}/scheduled-inspections/${scheduledInspectionId}`,
        data,
      )
      return response
    },
  },
  addRegisterItemAttachments: {
    mutationKey: null,
    mutationFn: async ({
      registerId,
      registerItemId,
      propertyId,
      files = [],
      siteboxPicks = [],
    }: RequestTypes['addRegisterItemAttachments']) => {
      const uploads = await Promise.all(
        files.map(async (file) => ({
          mediaKey: await s3Upload({
            file,
            folderName: 'register_item_attachments',
          }),
          filename: file.name,
        })),
      )
      const { data } = await APIAxiosInstance.post(
        `registers/${registerId}/items/${registerItemId}/attachments`,
        { propertyId, uploads, siteboxPicks },
      )
      return data
    },
  },
  renameRegisterItemAttachment: {
    mutationKey: null,
    mutationFn: async ({
      registerId,
      registerItemId,
      attachmentId,
      name,
    }: RequestTypes['renameRegisterItemAttachment']) => {
      const { data } = await APIAxiosInstance.post(
        `registers/${registerId}/items/${registerItemId}/attachments/${attachmentId}/rename`,
        { name },
      )
      return data
    },
  },
  archiveRegisterItemAttachment: {
    mutationKey: null,
    mutationFn: async ({
      registerId,
      registerItemId,
      attachmentId,
    }: RequestTypes['archiveRegisterItemAttachment']) => {
      const { data } = await APIAxiosInstance.post(
        `registers/${registerId}/items/${registerItemId}/attachments/${attachmentId}/archive`,
        {},
      )
      return data
    },
  },
  restoreRegisterItemAttachment: {
    mutationKey: null,
    mutationFn: async ({
      registerId,
      registerItemId,
      attachmentId,
    }: RequestTypes['restoreRegisterItemAttachment']) => {
      const { data } = await APIAxiosInstance.post(
        `registers/${registerId}/items/${registerItemId}/attachments/${attachmentId}/restore`,
        {},
      )
      return data
    },
  },
})

const registerItemKeys = mergeQueryKeys(
  registerItemsQueryKeys,
  registerItemsMutationKeys,
)['register-items']

export { registerItemKeys }
